import { useState, useEffect, useMemo } from 'react'
import { Loader2, FolderTree } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { useCreateSuite, useTestSuites } from '@/features/test-suites/api/useTestSuites'
import { logger } from '@/lib/utils/logger'
import type { DRD } from '@/types/requirement.types'

interface MicrosuiteDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    drd: DRD
    projectId: number
}

interface ProposedSuite {
    key: string
    name: string
    selected: boolean
    requirementCount: number
}

function deriveServiceName(sourceFile: string): string {
    const normalized = sourceFile.replace(/\\/g, '/')
    const parts = normalized.split('/')
    if (parts.length > 1) return parts[0]
    const dotIdx = parts[0].lastIndexOf('.')
    return dotIdx > 0 ? parts[0].slice(0, dotIdx) : parts[0]
}

export function MicrosuiteDialog({
                                     open,
                                     onOpenChange,
                                     drd,
                                     projectId,
                                 }: MicrosuiteDialogProps) {
    const [parentId, setParentId] = useState<string>(projectId.toString())
    const [suites, setSuites] = useState<ProposedSuite[]>([])
    const [progressState, setProgressState] = useState<{ current: number; total: number } | null>(null)
    const [error, setError] = useState<string | null>(null)

    const createSuite = useCreateSuite()
    const { data: existingSuites = [], isLoading: isLoadingSuites } = useTestSuites({
        project_id: projectId,
    })

    const proposedSuites = useMemo((): ProposedSuite[] => {
        if (!drd.requirements?.length) return []
        const groups = new Map<string, number>()
        for (const req of drd.requirements) {
            if (!req.source_file) continue
            const svc = deriveServiceName(req.source_file)
            groups.set(svc, (groups.get(svc) ?? 0) + 1)
        }
        return Array.from(groups.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([name, count]) => ({
                key: name,
                name,
                selected: true,
                requirementCount: count,
            }))
    }, [drd.requirements])

    useEffect(() => {
        if (open) {
            setSuites(proposedSuites)
            setParentId(projectId.toString())
            setError(null)
            setProgressState(null)
        }
    }, [open, proposedSuites, projectId])

    const selectedCount = suites.filter((s) => s.selected && s.name.trim()).length
    const allSelected = suites.length > 0 && suites.every((s) => s.selected)
    const isCreating = progressState !== null

    const toggleAll = () => {
        setSuites((prev) => prev.map((s) => ({ ...s, selected: !allSelected })))
    }

    const toggleSuite = (key: string) => {
        setSuites((prev) =>
            prev.map((s) => (s.key === key ? { ...s, selected: !s.selected } : s))
        )
    }

    const renameSuite = (key: string, name: string) => {
        setSuites((prev) =>
            prev.map((s) => (s.key === key ? { ...s, name } : s))
        )
    }

    const handleCreate = async () => {
        const selected = suites.filter((s) => s.selected && s.name.trim())
        if (!selected.length) {
            setError('Select at least one suite to create.')
            return
        }
        if (!parentId) {
            setError('Select a parent node.')
            return
        }

        setError(null)
        setProgressState({ current: 0, total: selected.length })

        let successCount = 0
        const failed: string[] = []

        for (let i = 0; i < selected.length; i++) {
            setProgressState({ current: i + 1, total: selected.length })
            try {
                await createSuite.mutateAsync({
                    name: selected[i].name.trim(),
                    parent_id: parseInt(parentId, 10),
                })
                successCount++
            } catch (err) {
                logger.error(`Failed to create suite "${selected[i].name}"`, err)
                failed.push(selected[i].name)
            }
        }

        setProgressState(null)

        if (failed.length === 0) {
            toast.success(
                `Created ${successCount} microsuite${successCount !== 1 ? 's' : ''} successfully.`
            )
            onOpenChange(false)
        } else if (successCount > 0) {
            toast.warning(
                `Created ${successCount} suite${successCount !== 1 ? 's' : ''}. Failed: ${failed.join(', ')}.`
            )
            onOpenChange(false)
        } else {
            setError(`All suites failed to create: ${failed.join(', ')}`)
        }
    }

    return (
        <Dialog open={open} onOpenChange={isCreating ? undefined : onOpenChange}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FolderTree className="h-5 w-5 text-purple-500" />
                        Create Microsuites
                    </DialogTitle>
                    <DialogDescription>
                        Suites derived from source file groupings in{' '}
                        <span className="font-medium text-foreground">{drd.title}</span>.
                        Select a parent suite, review names, then create.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    {error && (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {/* Parent selector */}
                    <div className="space-y-2">
                        <Label>Place under</Label>
                        <Select
                            value={parentId}
                            onValueChange={setParentId}
                            disabled={isCreating || isLoadingSuites}
                        >
                            <SelectTrigger>
                                <SelectValue
                                    placeholder={isLoadingSuites ? 'Loading suites…' : 'Select parent'}
                                />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={projectId.toString()}>
                                    📁 Project Root (top-level)
                                </SelectItem>
                                {existingSuites.map((s) => (
                                    <SelectItem key={s.id} value={s.id.toString()}>
                                        {s.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Suite list */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label>Proposed Suites ({suites.length})</Label>
                            <button
                                type="button"
                                onClick={toggleAll}
                                disabled={isCreating}
                                className="text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                            >
                                {allSelected ? 'Deselect all' : 'Select all'}
                            </button>
                        </div>

                        {suites.length === 0 ? (
                            <div className="rounded-md border border-dashed py-6 text-center text-sm text-muted-foreground">
                                No source files found in this DRD's requirements.
                            </div>
                        ) : (
                            <div className="rounded-md border divide-y max-h-[280px] overflow-y-auto">
                                {suites.map((suite) => (
                                    <div
                                        key={suite.key}
                                        className="flex items-center gap-3 px-3 py-2.5"
                                    >
                                        <Checkbox
                                            checked={suite.selected}
                                            onCheckedChange={() => toggleSuite(suite.key)}
                                            disabled={isCreating}
                                        />
                                        <Input
                                            value={suite.name}
                                            onChange={(e) => renameSuite(suite.key, e.target.value)}
                                            disabled={isCreating || !suite.selected}
                                            className="h-7 flex-1 text-sm"
                                        />
                                        <Badge variant="outline" className="text-xs flex-shrink-0">
                                            {suite.requirementCount} req
                                            {suite.requirementCount !== 1 ? 's' : ''}
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Progress */}
                    {isCreating && progressState && (
                        <div className="space-y-1.5">
                            <div className="flex justify-between text-xs text-muted-foreground">
                                <span>Creating suites…</span>
                                <span>
                  {progressState.current} / {progressState.total}
                </span>
                            </div>
                            <Progress
                                value={(progressState.current / progressState.total) * 100}
                                className="h-2"
                            />
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isCreating}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreate}
                        disabled={isCreating || selectedCount === 0}
                    >
                        {isCreating ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Creating…
                            </>
                        ) : (
                            `Create ${selectedCount} Suite${selectedCount !== 1 ? 's' : ''}`
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}